import { ArrowRight, Droplet, Layers, ShieldCheck, Mail } from "lucide-react";
import { IMAGES, CATEGORIES, PROTOCOL_STEPS, CONTACT_EMAIL } from "../data";

const catImages = { gels: IMAGES.nude, bases: IMAGES.pour, vernis: IMAGES.texture, fraises: IMAGES.fraises, nailart: IMAGES.nailart };
const catDesc = {
  gels: "Framework Gel et gels de construction teintés pour l'architecture de l'ongle.",
  bases: "Bases denses et top coats de finition, sans primer.",
  vernis: "Gels couleur pour des poses éclatantes après l'architecture.",
  fraises: "Embouts diamant et carbure de qualité professionnelle.",
  nailart: "Stickers et sliders pour des décors précis et rapides.",
};

export default function Home() {
  return (
    <>
      <section className="mx-auto max-w-6xl px-4 pb-20 pt-14 sm:px-8 lg:pt-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="tag-mono rise mb-6 text-[#E4C5B3]">Boutique en ligne — Produits pro · France</p>
            <h1 className="rise rise-1 font-display text-4xl font-medium leading-[1.08] tracking-tight text-[#F8F7F4] sm:text-5xl lg:text-6xl">
              Le geste précis commence par le <em className="not-italic text-[#E4C5B3]">bon produit</em>.
            </h1>
            <p className="rise rise-2 mt-6 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              AB Nails sélectionne pour les prothésistes ongulaires des gels de construction, vernis semi-permanents,
              fraises et accessoires de nail art de qualité professionnelle — dont la gamme américaine American Creator.
            </p>
            <div className="rise rise-3 mt-9 flex flex-wrap gap-4">
              <a href="/produits/" data-testid="hero-cta-produits" className="btn-primary">
                Découvrir les produits <ArrowRight size={16} aria-hidden="true" />
              </a>
              <a href="/protocole/" data-testid="hero-cta-protocole" className="btn-ghost">
                Voir le protocole d'application
              </a>
            </div>
            <div className="rise rise-4 mt-10 flex flex-wrap gap-x-6 gap-y-2">
              {["Gel de construction", "15 ml · 30 ml", "Teintes nude", "Usage professionnel"].map((t) => (
                <span key={t} className="tag-mono text-zinc-500">
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="rise rise-2 lg:col-span-5">
            <figure className="frame-editorial">
              <img
                src={IMAGES.hero.src}
                width={IMAGES.hero.width}
                height={IMAGES.hero.height}
                alt={IMAGES.hero.alt}
                fetchPriority="high"
                className="h-full w-full object-cover"
              />
            </figure>
          </div>
        </div>
      </section>

      <section aria-labelledby="gammes" className="border-t border-white/10 bg-[#15151C]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:py-24">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="tag-mono mb-3 text-[#E4C5B3]">Le catalogue</p>
              <h2 id="gammes" className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
                Cinq gammes, un niveau d'exigence
              </h2>
            </div>
            <a href="/produits/" className="link-arrow" data-testid="gammes-cta">
              Voir toutes les gammes <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`/produits/#${c.id}`}
                data-testid={`gamme-card-${c.id}`}
                className="group border border-white/10 bg-[#1C1C26] transition-colors hover:border-[#E4C5B3]/40"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={catImages[c.id].src}
                    width={catImages[c.id].width}
                    height={catImages[c.id].height}
                    alt={catImages[c.id].alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-medium text-[#F8F7F4]">{c.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{catDesc[c.id]}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="exigence" className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="tag-mono mb-3 text-[#E4C5B3]">Notre exigence</p>
            <h2 id="exigence" className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Une sélection pensée pour les professionnelles
            </h2>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              Chaque référence est choisie pour répondre aux gestes réels de la prothésie ongulaire :
              préparation à la ponceuse, architecture au gel, couleur et finition.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-3 lg:col-span-8">
            {[
              {
                icon: Layers,
                title: "Gammes complètes",
                text: "De la base dense au top coat, en passant par les gels de construction teintés : tout le protocole est couvert.",
              },
              {
                icon: ShieldCheck,
                title: "Marque reconnue",
                text: "Nous proposons notamment American Creator, marque américaine créée en 2014, aux formules hypoallergéniques, cruelty-free et vegan.",
              },
              {
                icon: Droplet,
                title: "Conseil d'application",
                text: "Un protocole détaillé, temps de catalysation inclus, pour exploiter chaque référence dans les règles de l'art.",
              },
            ].map((f) => (
              <div key={f.title} className="border border-white/10 bg-[#15151C] p-6">
                <f.icon size={22} className="text-[#D4AF37]" aria-hidden="true" />
                <h3 className="mt-4 font-display text-lg font-medium">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="protocole" className="border-t border-white/10 bg-[#15151C]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:py-24">
          <div className="mb-12 max-w-2xl">
            <p className="tag-mono mb-3 text-[#E4C5B3]">Protocole</p>
            <h2 id="protocole" className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Six étapes, de la préparation à la finition
            </h2>
          </div>
          <ol className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {PROTOCOL_STEPS.map((s) => (
              <li key={s.n} className="border border-white/10 bg-[#1C1C26] p-6">
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-3xl text-[#E4C5B3]/60">{s.n}</span>
                  <span className="tag-mono text-zinc-500">{s.tag}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-medium">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
              </li>
            ))}
          </ol>
          <a href="/protocole/" className="link-arrow mt-10 inline-flex" data-testid="protocole-cta">
            Le protocole complet, étape par étape <ArrowRight size={16} aria-hidden="true" />
          </a>
        </div>
      </section>

      <section aria-labelledby="contact" className="mx-auto max-w-6xl px-4 py-16 sm:px-8 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-10 border border-[#E4C5B3]/25 bg-[#1C1C26] p-8 sm:p-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="tag-mono mb-3 text-[#E4C5B3]">Contact</p>
            <h2 id="contact" className="font-display text-3xl font-medium tracking-tight sm:text-4xl">
              Une référence, un tarif, un conseil ?
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-zinc-400">
              Le catalogue évolue régulièrement. Écrivez-nous : nous vous répondrons avec les références
              et les tarifs en cours.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-cta" className="btn-primary">
              <Mail size={16} aria-hidden="true" /> {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
